import * as Yup from 'yup';
import { useMemo } from 'react';
import { max, isBefore, startOfDay } from 'date-fns';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Unstable_Grid2';
import { DatePicker } from '@mui/x-date-pickers';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import Iconify from 'src/components/iconify';
import { RHFSelect, RHFTextField, RHFRadioGroup } from 'src/components/hook-form';

import {
  LoanPlanType,
  LOAN_FREQUENCY_OPTIONS,
  LoanInstallmentFrequency,
} from 'src/types/loan-agreement';

import { toCents, previewEqualSchedule } from './utils';

export interface LoanPlanFormValues {
  planType: LoanPlanType;
  frequency: LoanInstallmentFrequency;
  installmentAmount: number;
  firstDueDate: Date | null;
  schedule: { dueDate: Date | null; amount: number }[];
}

export const LOAN_PLAN_DEFAULTS: LoanPlanFormValues = {
  planType: LoanPlanType.EQUAL,
  frequency: LoanInstallmentFrequency.MONTHLY,
  installmentAmount: 0,
  firstDueDate: null,
  schedule: [{ dueDate: null, amount: 0 }],
};

const PLAN_TYPE_OPTIONS = [
  { value: LoanPlanType.EQUAL, label: 'Equal installments' },
  { value: LoanPlanType.CUSTOM, label: 'Custom plan' },
];

const isPlanActive = (
  planRequired: boolean | undefined,
  planType: LoanPlanType,
  type: LoanPlanType
) => planRequired !== false && planType === type;

/**
 * Yup rules for the plan fields. Expects `capital` (rupees) and
 * `loanPaidDate` as sibling fields, and a `planRequired` resolver context
 * flag (the edit form only validates the plan when the capital changes).
 */
export const loanPlanSchemaShape = {
  planType: Yup.mixed<LoanPlanType>().oneOf(Object.values(LoanPlanType)).required(),
  frequency: Yup.mixed<LoanInstallmentFrequency>()
    .oneOf(Object.values(LoanInstallmentFrequency))
    .required(),
  installmentAmount: Yup.number().when(
    ['$planRequired', 'planType'],
    ([planRequired, planType], schema) =>
      isPlanActive(planRequired, planType, LoanPlanType.EQUAL)
        ? schema
            .moreThan(0, 'Installment amount is required')
            .test(
              'lte-capital',
              'Installment amount cannot exceed the loan capital',
              function check(value) {
                return toCents(value) <= toCents(this.parent.capital);
              }
            )
        : schema.notRequired()
  ),
  firstDueDate: Yup.date()
    .nullable()
    .when(['$planRequired', 'planType'], ([planRequired, planType], schema) =>
      isPlanActive(planRequired, planType, LoanPlanType.EQUAL)
        ? schema
            .required('First installment date is required')
            .test('after-paid-date', 'Cannot be before the loan paid date', function check(value) {
              const paid = this.parent.loanPaidDate as Date | null;
              return !value || !paid || !isBefore(startOfDay(value), startOfDay(paid));
            })
        : schema.notRequired()
    ),
  schedule: Yup.array()
    .of(Yup.object({ dueDate: Yup.date().nullable(), amount: Yup.number() }))
    .when(['$planRequired', 'planType'], ([planRequired, planType], schema) =>
      isPlanActive(planRequired, planType, LoanPlanType.CUSTOM)
        ? schema.test('valid-rows', '', function check(rows) {
            const list = rows ?? [];
            const paid = this.parent.loanPaidDate as Date | null;
            for (let i = 0; i < list.length; i += 1) {
              const { dueDate, amount } = list[i];
              if (!dueDate) {
                return this.createError({ message: `Installment ${i + 1} needs a date` });
              }
              if (!amount || amount <= 0) {
                return this.createError({ message: `Installment ${i + 1} needs an amount` });
              }
              if (paid && isBefore(startOfDay(dueDate), startOfDay(paid))) {
                return this.createError({
                  message: `Installment ${i + 1} is before the loan paid date`,
                });
              }
              const previous = list[i - 1]?.dueDate;
              if (previous && !isBefore(startOfDay(previous), startOfDay(dueDate))) {
                return this.createError({
                  message: 'Installment dates must be in ascending order with no duplicates',
                });
              }
            }
            const total = list.reduce((sum, row) => sum + toCents(row.amount), 0);
            if (total !== toCents(this.parent.capital)) {
              return this.createError({
                message: 'The installments must add up to exactly the loan capital',
              });
            }
            return true;
          })
        : schema
    ),
};

/**
 * Converts the plan form values into the API payload.
 */
export function toLoanPlanPayload(values: LoanPlanFormValues) {
  if (values.planType === LoanPlanType.EQUAL) {
    return {
      planType: LoanPlanType.EQUAL,
      frequency: values.frequency,
      installmentAmountInCents: toCents(values.installmentAmount),
      firstDueDate: values.firstDueDate?.toISOString(),
    };
  }
  return {
    planType: LoanPlanType.CUSTOM,
    schedule: values.schedule.map((row) => ({
      dueDate: (row.dueDate as Date).toISOString(),
      amountInCents: toCents(row.amount),
    })),
  };
}

type Props = {
  capitalInCents: number;
  // Installment dates can't be before the day the loan was paid out.
  earliestDueDate: Date | null;
  disabled?: boolean;
};

/**
 * Equal (frequency + amount + first date, auto-generated preview) or
 * Custom (row-by-row) repayment plan. Either way the rows must add up to
 * exactly the capital. Used on create and on an admin capital change.
 */
export default function LoanPlanFields({ capitalInCents, earliestDueDate, disabled }: Props) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const scheduleError =
    (errors.schedule as { message?: string; root?: { message?: string } })?.message ??
    (errors.schedule as { root?: { message?: string } })?.root?.message;

  const { fields, append, remove } = useFieldArray({ control, name: 'schedule' });

  const planType: LoanPlanType = watch('planType');
  const frequency: LoanInstallmentFrequency = watch('frequency');
  const installmentAmount: number = watch('installmentAmount');
  const firstDueDate: Date | null = watch('firstDueDate');
  const schedule: LoanPlanFormValues['schedule'] = watch('schedule');

  const equalPreview = useMemo(
    () => previewEqualSchedule(capitalInCents, toCents(installmentAmount), firstDueDate, frequency),
    [capitalInCents, installmentAmount, firstDueDate, frequency]
  );

  const allocatedInCents = schedule.reduce((sum, row) => sum + toCents(row.amount), 0);
  const leftInCents = capitalInCents - allocatedInCents;

  // Each custom row's date must come after the previous one.
  const minDateForRow = (index: number) => {
    const previous = schedule[index - 1]?.dueDate;
    const candidates = [earliestDueDate, previous].filter((d): d is Date => !!d);
    return candidates.length ? max(candidates) : undefined;
  };

  return (
    <Stack
      spacing={2}
      aria-disabled={disabled}
      sx={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
    >
      <RHFRadioGroup row name="planType" options={PLAN_TYPE_OPTIONS} />

      {planType === LoanPlanType.EQUAL && (
        <>
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <RHFSelect name="frequency" label="Frequency" disabled={disabled}>
                {LOAN_FREQUENCY_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </RHFSelect>
            </Grid>

            <Grid xs={12} md={4}>
              <RHFTextField
                name="installmentAmount"
                label="Installment Amount (Rupees)"
                type="number"
                disabled={disabled}
                required
              />
            </Grid>

            <Grid xs={12} md={4}>
              <Controller
                name="firstDueDate"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <DatePicker
                    label="First Installment Date"
                    value={field.value}
                    onChange={field.onChange}
                    minDate={earliestDueDate ?? undefined}
                    disabled={disabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary">
            If the installments aren&apos;t all the same amount or aren&apos;t evenly spaced, use a
            custom plan. The last installment covers whatever is left, so it can be smaller.
          </Typography>

          {equalPreview.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Due Date</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {equalPreview.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{fDate(row.dueDate)}</TableCell>
                    <TableCell align="right">{fCurrencyRupees(row.amountInCents)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>
                    Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {fCurrencyRupees(capitalInCents)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </>
      )}

      {planType === LoanPlanType.CUSTOM && (
        <>
          {fields.map((field, index) => (
            <Stack key={field.id} direction="row" spacing={2} alignItems="flex-start">
              <Typography variant="subtitle2" sx={{ pt: 2, minWidth: 24 }}>
                {index + 1}.
              </Typography>

              <Controller
                name={`schedule.${index}.dueDate`}
                control={control}
                render={({ field: dateField, fieldState: { error } }) => (
                  <DatePicker
                    label="Installment Date"
                    value={dateField.value}
                    onChange={dateField.onChange}
                    minDate={minDateForRow(index)}
                    disabled={disabled}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />

              <RHFTextField
                name={`schedule.${index}.amount`}
                label="Amount (Rupees)"
                type="number"
                disabled={disabled}
                required
              />

              <IconButton
                color="error"
                onClick={() => remove(index)}
                disabled={disabled || fields.length === 1}
                sx={{ mt: 1 }}
              >
                <Iconify icon="solar:trash-bin-trash-bold" />
              </IconButton>
            </Stack>
          ))}

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Button
              size="small"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={() => append({ dueDate: null, amount: 0 })}
              disabled={disabled || leftInCents <= 0}
            >
              Add Installment
            </Button>

            <Typography variant="body2" color="text.secondary">
              Allocated {fCurrencyRupees(allocatedInCents)} of {fCurrencyRupees(capitalInCents)}
            </Typography>
          </Stack>

          {scheduleError && leftInCents === 0 && <Alert severity="error">{scheduleError}</Alert>}

          {capitalInCents > 0 && leftInCents !== 0 && (
            <Alert severity={leftInCents > 0 ? 'info' : 'error'}>
              {leftInCents > 0
                ? `${fCurrencyRupees(
                    leftInCents
                  )} left to allocate — the installments must add up to the full capital.`
                : `The installments exceed the capital by ${fCurrencyRupees(-leftInCents)}.`}
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
}
